import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {User} from "@vesta/vos-types/modules/workspaceLevel/users/domain/User";
import type {UserTemplate} from "@vesta/vos-types/modules/workspaceLevel/users/domain/UserTemplate";
import {formatDateTime, getUserDisplayName, type PlatformUserDisplayDetails} from "~/utilities/utilities";

type SystemColumnKey = "createdAt" | "createdBy" | "updatedAt" | "updatedBy";

type SystemColumnStringValueContext = {
    platformUserDetailsByUserId?: ReadonlyMap<Uuid, PlatformUserDisplayDetails>;
    userTemplates: Array<UserTemplate>;
    users: Array<User>;
    value: unknown;
};

type SystemColumnDetails = {
    displayName: string;
    stringValueGetter: (context: SystemColumnStringValueContext) => string;
};

function timestampToDisplayString(value: unknown): string {
    return typeof value === "string" && value !== "" ? formatDateTime(value) : "-";
}

function userReferenceToDisplayString({
    value,
    users,
    userTemplates,
    platformUserDetailsByUserId,
}: SystemColumnStringValueContext): string {
    return getUserDisplayName(
        typeof value === "string" ? value as Uuid : null,
        users,
        userTemplates,
        platformUserDetailsByUserId,
    );
}

export const systemAdditionalColumnTypeToDetailsMap: Record<SystemColumnKey, SystemColumnDetails> = {
    createdAt: {
        displayName: "Timestamp",
        stringValueGetter: ({value}) => timestampToDisplayString(value),
    },
    createdBy: {
        displayName: "User",
        stringValueGetter: userReferenceToDisplayString,
    },
    updatedAt: {
        displayName: "Timestamp",
        stringValueGetter: ({value}) => timestampToDisplayString(value),
    },
    updatedBy: {
        displayName: "User",
        stringValueGetter: userReferenceToDisplayString,
    },
};

export function getSystemAdditionalColumnDetails(columnId: string): SystemColumnDetails | undefined {
    switch (columnId) {
        case "createdAt":
        case "createdBy":
        case "updatedAt":
        case "updatedBy":
            return systemAdditionalColumnTypeToDetailsMap[columnId];
        default:
            return undefined;
    }
}
